import { Button, Modal, Spinner, TextInput } from "flowbite-react";
import { useCallback, useEffect, useState } from "react";

const SEARCH_DEBOUNCE_MS = 250;

export const ImpersonationPickerModal = ({
  isOpen,
  onClose,
  searchCandidates,
  onStart,
}) => {
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const results = await searchCandidates(query);
        setCandidates(results);
      } catch (_err) {
        setCandidates([]);
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [isOpen, query, searchCandidates]);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setSelected(null);
      setPassword("");
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!selected || !password) return;
      setSubmitting(true);
      setError(null);
      try {
        await onStart({ targetUserId: selected.id, password });
      } catch (err) {
        const status = err.response?.status;
        if (status === 401) setError("Invalid password.");
        else if (status === 403)
          setError("Not allowed — refresh and try again.");
        else if (status === 404)
          setError("Target user not found, or feature disabled.");
        else if (status === 400)
          setError(
            err.response?.data?.error || "Cannot impersonate this user.",
          );
        else setError("Something went wrong starting impersonation.");
        setSubmitting(false);
      }
    },
    [onStart, password, selected],
  );

  return (
    <Modal show={isOpen} onClose={onClose} size="md">
      <Modal.Header>Impersonate user</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="impersonation-query"
            >
              Search by email
            </label>
            <TextInput
              id="impersonation-query"
              type="search"
              placeholder="customer@example.com"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelected(null);
              }}
              autoFocus
            />
          </div>

          <div className="max-h-48 overflow-y-auto rounded border border-gray-200">
            {searching ? (
              <div className="flex items-center justify-center p-4">
                <Spinner size="sm" />
              </div>
            ) : candidates.length === 0 ? (
              <p className="p-3 text-sm text-gray-500">No matching users.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {candidates.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(c)}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                        selected?.id === c.id ? "bg-yellow-50" : ""
                      }`}
                    >
                      <div className="font-medium">{c.email}</div>
                      {c.team_name && (
                        <div className="text-xs text-gray-500">
                          {c.team_name}
                        </div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="impersonation-password"
            >
              Your password
            </label>
            <TextInput
              id="impersonation-password"
              type="password"
              placeholder="Re-enter your password to confirm"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <p className="mt-1 text-xs text-gray-500">
              Required to confirm it's you. Impersonation is logged and
              time-limited to 60 minutes.
            </p>
          </div>

          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button color="gray" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button
              color="failure"
              type="submit"
              disabled={!selected || !password || submitting}
            >
              {submitting
                ? "Starting…"
                : `Impersonate ${selected ? selected.email : ""}`}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
